import { useEffect, useState } from 'react';
import getConfig from 'next/config';

import type { GradingExperimentVehicle } from 'Context/GradingExperimentContext';
import type { VehicleDetails } from 'Pages/vehicles/helpers/vehicle.schema';
import { logger } from 'Services/logger/logger';
import { EXPERIMENTS, EXPERIMENTS_DECISIONS, EXPERIMENTS_RULE_KEYS } from 'Services/optimizely/optimizely.consts';
import { useOptimizelyDecisionWithCustomUserId } from 'Services/optimizely/useOptimizelyDecisionWithCustomUserId';
import dayjs from 'Utilities/dayjs';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useIsUserPowerBuyer from 'Utilities/hooks/useIsUserPowerBuyer';
import useUser from 'Utilities/hooks/useUser';

import { useOptimizely } from '../useOptimizely';

const { publicRuntimeConfig } = getConfig();

const { GRADING_AA_EXPERIMENT_CUTOFF_DATE } = publicRuntimeConfig;

export const NEW_GRADING_RELEASE_DATE = '2025-12-08T16:30:00Z';

const getIsVehicleEligibleForAAGrading = (createdAt: VehicleDetails['createdAt']) => {
	const parsedCreatedAtDate = dayjs(createdAt);
	const parsedAAExperimentCutoffDate = dayjs(GRADING_AA_EXPERIMENT_CUTOFF_DATE);

	return Boolean(
		createdAt &&
			GRADING_AA_EXPERIMENT_CUTOFF_DATE &&
			parsedCreatedAtDate.isValid() &&
			parsedAAExperimentCutoffDate.isValid() &&
			parsedCreatedAtDate.isSameOrAfter(parsedAAExperimentCutoffDate),
	);
};

export const useUpdatedGradingExperimentation = ({ vehicle }: { vehicle?: GradingExperimentVehicle } = {}) => {
	const shouldForceNewGrading = useFeatureToggle(FEATURES.forceNewGrading);
	const [isReadyToTrackMeasuringDecision, setIsReadyToTrackMeasuringDecision] = useState(false);
	const optimizelyClient = useOptimizely();
	const { dealerId } = useUser();
	const isUserPowerBuyer = useIsUserPowerBuyer();

	const consistencyAttributes = {
		isUserPowerBuyer,
	};

	// Consistency decision - The one we use to modify UI (always runs)
	const { decision: consistencyDecision, isEnabled: isConsistencyEnabled } = useOptimizelyDecisionWithCustomUserId({
		attributes: consistencyAttributes,
		experimentId: EXPERIMENTS.UPDATED_GRADING_CONSISTENCY_EXPERIMENT,
		userId: dealerId,
	});

	// We plan on removing this when dealers are used to the new grading
	const isVehicleSoldBeforeLaunchDate = Boolean(
		vehicle?.ratings.soldDateISO &&
			dayjs(vehicle.ratings.soldDateISO).isValid() &&
			dayjs(vehicle.ratings.soldDateISO).isBefore(NEW_GRADING_RELEASE_DATE),
	);

	const shouldTrackMeasuringDecision = Boolean(
		vehicle?.id && !isVehicleSoldBeforeLaunchDate && isConsistencyEnabled && consistencyDecision,
	);
	const measuringUserId = `${dealerId}-${vehicle?.id}`;
	useEffect(() => {
		if (optimizelyClient && shouldTrackMeasuringDecision) {
			optimizelyClient.setForcedVariation(
				EXPERIMENTS_RULE_KEYS.UPDATED_GRADING_MEASURING_EXPERIMENT.UPDATED_GRADING_MEASURING_AA_TEST_ROLLOUT,
				measuringUserId,
				consistencyDecision,
			);

			optimizelyClient.setForcedVariation(
				EXPERIMENTS_RULE_KEYS.UPDATED_GRADING_MEASURING_EXPERIMENT.UPDATED_GRADING_MEASURING_AB_TEST_ROLLOUT,
				measuringUserId,
				consistencyDecision,
			);

			setIsReadyToTrackMeasuringDecision(true);
		}
	}, [optimizelyClient, consistencyDecision, measuringUserId, shouldTrackMeasuringDecision]);

	const attributes = {
		dealership_id: dealerId,
		isEligibleForAAGradingExperiment: vehicle
			? getIsVehicleEligibleForAAGrading(vehicle.details.createdAt) && isReadyToTrackMeasuringDecision
			: false,
		// To be implemented in other ticket based on numericGrade attribute on the vehicle
		isEligibleForABGradingExperiment: Boolean(vehicle?.ratings?.numericGrade && isReadyToTrackMeasuringDecision),
	};

	// Measuring decision - The one we track metrics with but don't use to modify UI
	const { decision: measuringDecision, isEnabled: isMeasuringEnabled } = useOptimizelyDecisionWithCustomUserId({
		attributes,
		experimentId: EXPERIMENTS.UPDATED_GRADING_MEASURING_EXPERIMENT,
		userId: measuringUserId,
	});

	if (
		shouldTrackMeasuringDecision &&
		isReadyToTrackMeasuringDecision &&
		isMeasuringEnabled &&
		consistencyDecision !== measuringDecision
	) {
		const message =
			'Mismatch between the grading consistency and measuring experiment, check the Optimizely flag setup. ';

		logger.warn({
			context: { consistencyDecision, measuringDecision },
			message,
			scope: 'useUpdatedGradingExperimentation',
		});
	}

	// Ignore previously sold vehicles
	const isInNewGradingCohort =
		!isVehicleSoldBeforeLaunchDate &&
		isConsistencyEnabled &&
		consistencyDecision === EXPERIMENTS_DECISIONS.UPDATED_GRADING_CONSISTENCY_EXPERIMENT.SHOW_NEW_GRADING;

	return { isInNewGradingCohort: shouldForceNewGrading || isInNewGradingCohort };
};
